// src/pages/JurisdictionsPage.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { LineChart } from "@mui/x-charts/LineChart";
import jurisdictionApi from "../api/jurisdictionApi";
import { trackResultClicked } from "../analytics/ga";

const PAGE_SIZE = 50;
const YEAR_END = 2023;
const YEAR_START = YEAR_END - 4;
const YEARS = Array.from({ length: YEAR_END - YEAR_START + 1 }, (_, i) => YEAR_START + i);
const METRIC_CODES = {
  FRASER_PPI: "fraser_ppi",
  POLITICAL_STABILITY: "political_stability",
};

export default function JurisdictionsPage() {
  const theme = useTheme();

  const [jurisdictions, setJurisdictions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedJurisdiction, setSelectedJurisdiction] = useState(null);

  const [filters, setFilters] = useState({
    q: "",
    continent: "",
  });
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState({
    key: "default",
    direction: "asc",
  });

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      try {
        const data = await jurisdictionApi.getJurisdictions();
        const rows = Array.isArray(data) ? data : data?.results ?? [];
        if (mounted) setJurisdictions(rows);
      } catch (err) {
        console.error("Failed to load jurisdictions", err);
        if (mounted) setJurisdictions([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const continents = useMemo(() => {
    const values = new Set();
    jurisdictions.forEach((j) => {
      if (j.continent) values.add(j.continent);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [jurisdictions]);

  const isFraserImputed = (metricValues) =>
    (metricValues || []).some(
      (entry) => entry.metric === METRIC_CODES.FRASER_PPI && entry.is_imputed_continent
    );

  const compareNullable = (a, b, direction = "asc") => {
    const dir = direction === "desc" ? -1 : 1;
    const aMissing = a === null || a === undefined || a === "";
    const bMissing = b === null || b === undefined || b === "";
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    if (typeof a === "number" && typeof b === "number") return (a - b) * dir;
    return String(a).localeCompare(String(b)) * dir;
  };

  const filtered = useMemo(() => {
    const q = (filters.q || "").trim().toLowerCase();
    const rows = [...jurisdictions].sort((a, b) => {
      const aImputed = isFraserImputed(a.metric_values) ? 1 : 0;
      const bImputed = isFraserImputed(b.metric_values) ? 1 : 0;
      if (aImputed !== bImputed) return aImputed - bImputed;
      const aId = Number(a.id);
      const bId = Number(b.id);
      if (Number.isFinite(aId) && Number.isFinite(bId) && aId !== bId) {
        return aId - bId;
      }
      const aKey = `${a.continent || ""} ${a.country || ""} ${a.region_or_state || ""}`.trim();
      const bKey = `${b.continent || ""} ${b.country || ""} ${b.region_or_state || ""}`.trim();
      return aKey.localeCompare(bKey);
    });
    return rows.filter((j) => {
      if (filters.continent && j.continent !== filters.continent) return false;
      if (!q) return true;
      const haystack = [
        j.continent,
        j.country,
        j.region_or_state,
        j.iso_country_code,
        j.iso_3166_2,
        j.type,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [jurisdictions, filters.q, filters.continent]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filters.q, filters.continent]);

  const fmtScore = (v) => {
    if (v === null || v === undefined || v === "") return "-";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return String(Math.round(n));
  };

  const getScoreBucket = (score) => {
    if (!Number.isFinite(score)) return null;
    if (score < 20) return 0;
    if (score < 40) return 1;
    if (score < 60) return 2;
    if (score < 80) return 3;
    return 4;
  };

  const getScoreStyle = (rawScore, reverse = false) => {
    if (rawScore === null || rawScore === undefined || rawScore === "") {
      return { backgroundColor: "transparent", color: "inherit" };
    }
    const score = Number(rawScore);
    if (!Number.isFinite(score)) {
      return { backgroundColor: "transparent", color: "inherit" };
    }
    const bucket = getScoreBucket(score);
    if (bucket === null) return { backgroundColor: "transparent", color: "inherit" };
    const palette = ["#3cb371", "#a7dca8", "#ffe082", "#f2b2a0", "#e57373"];
    const idx = reverse ? 4 - bucket : bucket;
    const backgroundColor = palette[idx] ?? palette[2];
    return {
      backgroundColor,
      color: theme.palette.getContrastText(backgroundColor),
    };
  };

  const getMetricSeries = (metricValues, metricCode) => {
    const byYear = new Map();
    (metricValues || []).forEach((entry) => {
      if (entry.metric !== metricCode) return;
      const year = Number(entry.year);
      const value = Number(entry.value);
      if (!Number.isFinite(year)) return;
      byYear.set(year, Number.isFinite(value) ? value : null);
    });
    return YEARS.map((year) => (byYear.has(year) ? byYear.get(year) : null));
  };

  const getLatestMetricValue = (metricValues, metricCode) => {
    const rows = (metricValues || []).filter((entry) => entry.metric === metricCode);
    if (!rows.length) return null;
    const year2023 = rows.find((entry) => Number(entry.year) === YEAR_END);
    if (year2023) return year2023.value;
    const latest = rows.reduce((acc, entry) => (entry.year > acc.year ? entry : acc), rows[0]);
    return latest.value;
  };

  const sorted = useMemo(() => {
    const data = [...filtered];
    const dir = sortConfig.direction;
    if (sortConfig.key === "default") {
      return data;
    }
    return data.sort((a, b) => {
      if (sortConfig.key === "continent") {
        return compareNullable(a.continent, b.continent, dir);
      }
      if (sortConfig.key === "country") {
        return compareNullable(a.country, b.country, dir);
      }
      if (sortConfig.key === "region_or_state") {
        return compareNullable(a.region_or_state, b.region_or_state, dir);
      }
      if (sortConfig.key === "fraser_ppi") {
        const aVal = Number(getLatestMetricValue(a.metric_values, METRIC_CODES.FRASER_PPI));
        const bVal = Number(getLatestMetricValue(b.metric_values, METRIC_CODES.FRASER_PPI));
        return compareNullable(
          Number.isFinite(aVal) ? aVal : null,
          Number.isFinite(bVal) ? bVal : null,
          dir
        );
      }
      if (sortConfig.key === "political_stability") {
        const aVal = Number(getLatestMetricValue(a.metric_values, METRIC_CODES.POLITICAL_STABILITY));
        const bVal = Number(getLatestMetricValue(b.metric_values, METRIC_CODES.POLITICAL_STABILITY));
        return compareNullable(
          Number.isFinite(aVal) ? aVal : null,
          Number.isFinite(bVal) ? bVal : null,
          dir
        );
      }
      return compareNullable(Number(a.id), Number(b.id), dir);
    });
  }, [filtered, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const isSame = prev.key === key;
      const nextDirection = isSame && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction: nextDirection };
    });
  };

  const handleRowClick = (jurisdiction, position) => {
    trackResultClicked({
      result_type: "jurisdiction",
      result_id: jurisdiction?.canonical_key,
      position: Number.isFinite(position) ? position + 1 : undefined,
      query: filters.q,
    });
    setSelectedJurisdiction(jurisdiction);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedJurisdiction(null);
  };

  const selectedTitle = selectedJurisdiction
    ? selectedJurisdiction.region_or_state
      ? `${selectedJurisdiction.country} - ${selectedJurisdiction.region_or_state}`
      : selectedJurisdiction.country
    : "";

  const selectedMetrics = selectedJurisdiction?.metric_values || [];
  const latestFraser = getLatestMetricValue(selectedMetrics, METRIC_CODES.FRASER_PPI);
  const latestStability = getLatestMetricValue(selectedMetrics, METRIC_CODES.POLITICAL_STABILITY);
  const fraserSeries = getMetricSeries(selectedMetrics, METRIC_CODES.FRASER_PPI);
  const stabilitySeries = getMetricSeries(selectedMetrics, METRIC_CODES.POLITICAL_STABILITY);
  const hasFraserData = fraserSeries.some((value) => value !== null && value !== undefined);
  const hasStabilityData = stabilitySeries.some((value) => value !== null && value !== undefined);

  return (
    <>
      <Container sx={{ mt: 6, mb: 8 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="h4" fontWeight={700}>
              Jurisdictions
            </Typography>
          </Box>
          <Box sx={{ mb: 2, mx:1 }}>
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                backgroundColor: "transparent",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon fontSize="small" />}
                sx={{ px: 1, py: 1, minHeight: "auto", "& .MuiAccordionSummary-content": { m: 0 } }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <InfoOutlinedIcon fontSize="small" />
                  <Typography variant="subtitle2">Data Sources</Typography>
                </Stack>
              </AccordionSummary>

                <AccordionDetails sx={{ px: 2, pt: 1 }}>
                <Stack spacing={0.75}>
                  <Stack spacing={0.4}>
                    <Typography variant="body2" color="text.secondary">
                      <b>Jurisdictions</b> are defined at the continent, country and region/state/province level. 
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <b>Fraser Mining Policy Perception Index (PPI)</b> provides jurisdiction-level policy
                      perception scores from the Fraser Institute.
                      Some jurisdictions have PPI values imputed from continent-level averages when they
                      likely do not have mining operations, included for completeness.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <b>World Bank Political Stability Index</b> captures political stability and absence
                      of violence/terrorism. For regions, states, or provinces, political stability values 
                      are sourced at the country level.
                    </Typography>
                  </Stack>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>

          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              size="small"
              label="Search"
              value={filters.q}
              onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
              placeholder="Continent, country, region, ISO..."
              sx={{ width: 360, maxWidth: "100%" }}
            />
            <TextField
              select
              size="small"
              label="Continent"
              value={filters.continent}
              onChange={(e) => setFilters((prev) => ({ ...prev, continent: e.target.value }))}
              sx={{ width: 220 }}
            >
              <MenuItem value="">All</MenuItem>
              {continents.map((continent) => (
                <MenuItem key={continent} value={continent}>
                  {continent}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="body2" color="text.secondary">
              {loading && "Loading..."}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            ...theme.mixins.frostedBox,
            mt: 2,
            overflow: "hidden",
          }}
        >
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortConfig.key === "continent"}
                      direction={sortConfig.key === "continent" ? sortConfig.direction : "asc"}
                      onClick={() => handleSort("continent")}
                    >
                      Continent
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortConfig.key === "country"}
                      direction={sortConfig.key === "country" ? sortConfig.direction : "asc"}
                      onClick={() => handleSort("country")}
                    >
                      Country
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <TableSortLabel
                      active={sortConfig.key === "region_or_state"}
                      direction={sortConfig.key === "region_or_state" ? sortConfig.direction : "asc"}
                      onClick={() => handleSort("region_or_state")}
                    >
                      Region
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    <TableSortLabel
                      active={sortConfig.key === "fraser_ppi"}
                      direction={sortConfig.key === "fraser_ppi" ? sortConfig.direction : "asc"}
                      onClick={() => handleSort("fraser_ppi")}
                    >
                      Fraser Mining Policy Perception (2023)
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    <TableSortLabel
                      active={sortConfig.key === "political_stability"}
                      direction={sortConfig.key === "political_stability" ? sortConfig.direction : "asc"}
                      onClick={() => handleSort("political_stability")}
                    >
                      World Bank Political Stability (2023)
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sorted.slice(0, visibleCount).map((j, index) => (
                  <TableRow
                    key={j.canonical_key}
                    hover
                    sx={{
                      cursor: "pointer",
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                    onClick={() => handleRowClick(j, index)}
                  >
                    {(() => {
                      const fraserRaw = getLatestMetricValue(j.metric_values, METRIC_CODES.FRASER_PPI);
                      const stabilityRaw = getLatestMetricValue(
                        j.metric_values,
                        METRIC_CODES.POLITICAL_STABILITY
                      );
                      return (
                        <>
                    <TableCell>{j.continent || "-"}</TableCell>
                    <TableCell>{j.country || "-"}</TableCell>
                    <TableCell>{j.region_or_state || "-"}</TableCell>
                    <TableCell
                      align="center"
                      sx={getScoreStyle(fraserRaw, true)}
                    >
                      {fmtScore(fraserRaw)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={getScoreStyle(stabilityRaw, true)}
                    >
                      {fmtScore(stabilityRaw)}
                    </TableCell>
                        </>
                      );
                    })()}
                  </TableRow>
                ))}

                {!loading && sorted.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No jurisdictions found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>
        {sorted.length > visibleCount && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
            >
              Show more
            </Button>
          </Box>
        )}
      </Container>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        slotProps={{ paper: { sx: { width: { xs: "100%", sm: 620 } } } }}
        ModalProps={{
          BackdropProps: {
            sx: { backgroundColor: "rgba(0,0,0,0.25)" },
          },
        }}
      >
        {selectedJurisdiction && (
          <Box sx={{ p: 3, display: "flex", flexDirection: "column", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {selectedTitle}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedJurisdiction.continent || "Unknown continent"}
                </Typography>
              </Box>
              <IconButton onClick={closeDrawer} size="small" aria-label="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={3} sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Latest scores (2023)
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Box
                    sx={{
                      flex: 1,
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      ...getScoreStyle(latestFraser, true),
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 700, textAlign:'center' }}>
                      Fraser Mining Policy Perception
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5, textAlign:'center' }}>
                      {fmtScore(latestFraser)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      ...getScoreStyle(latestStability, true),
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 700, textAlign:'center' }}>
                      World Bank Political Stability
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5, textAlign:'center' }}>
                      {fmtScore(latestStability)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Fraser Mining Policy Perception (2019-2023)
                </Typography>
                {hasFraserData ? (
                  <LineChart
                    height={240}
                    xAxis={[{ data: YEARS, scaleType: "point", label: "Year" }]}
                    series={[
                      {
                        data: fraserSeries,
                        label: "Fraser PPI",
                        color: theme.palette.primary.main,
                      },
                    ]}
                    margin={{ left: 60, right: 20, top: 20, bottom: 40 }}
                    grid={{ horizontal: true }}
                    slotProps={{
                              legend: {
                                direction: 'horizontal',
                                position: { 
                                  vertical: 'bottom',
                                  horizontal: 'center'
                                }
                              }
                            }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No Fraser PPI data available for 2019-2023.
                  </Typography>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  World Bank Political Stability Index (2019-2023)
                </Typography>
                {hasStabilityData ? (
                  <LineChart
                    height={240}
                    xAxis={[{ data: YEARS, scaleType: "point", label: "Year" }]}
                    series={[
                      {
                        data: stabilitySeries,
                        label: "Political Stability",
                        color: theme.palette.secondary.main,
                      },
                    ]}
                    margin={{ left: 60, right: 20, top: 20, bottom: 40 }}
                    grid={{ horizontal: true }}
                    slotProps={{
                              legend: {
                                direction: 'horizontal',
                                position: { 
                                  vertical: 'bottom',
                                  horizontal: 'center'
                                }
                              }
                            }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No Political Stability data available for 2019-2023.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        )}
      </Drawer>
    </>
  );
}
